import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle, XCircle } from 'lucide-react';

/**
 * Full-screen overlay for success/failure animations after submission
 * Features weapon-specific dinosaur capture animations
 */
const ResultOverlay = ({
  type,
  dinosaurName,
  dinosaurImage,
  weapon,
  testResults,
  onComplete,
  autoNavigateDelay = 7000
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [phase, setPhase] = useState(0); // 0: aim, 1: fire weapon, 2: hit, 3: capture, 4: result

  const isSuccess = type === 'success';

  useEffect(() => {
    requestAnimationFrame(() => {
      setIsVisible(true);
    });

    // Phase progression for weapon animation
    const timers = [
      setTimeout(() => setPhase(1), 800),   // Fire weapon
      setTimeout(() => setPhase(2), 1800),  // Hit target
      setTimeout(() => setPhase(3), 2800),  // Capture effect
      setTimeout(() => setPhase(4), 4000),  // Show result
    ];

    const completeTimer = setTimeout(() => {
      setIsVisible(false);
      setTimeout(() => onComplete?.(), 500);
    }, autoNavigateDelay);

    return () => {
      timers.forEach(clearTimeout);
      clearTimeout(completeTimer);
    };
  }, [autoNavigateDelay, onComplete]);

  // Weapon-specific projectile/effect components
  const WeaponEffect = () => {
    const weaponType = weapon?.toLowerCase() || '';

    // Tranquilizer Dart
    if (weaponType.includes('tranquilizer')) {
      return (
        <motion.div
          className="absolute left-0 top-1/2 -translate-y-1/2"
          initial={{ x: -100, opacity: 0 }}
          animate={{ x: 250, opacity: [0, 1, 1, 0] }}
          transition={{ duration: 0.8, ease: "easeIn" }}
        >
          {/* Dart body */}
          <svg width="80" height="24" viewBox="0 0 80 24">
            <defs>
              <linearGradient id="dartGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#ef4444" />
                <stop offset="50%" stopColor="#dc2626" />
                <stop offset="100%" stopColor="#991b1b" />
              </linearGradient>
            </defs>
            {/* Needle */}
            <polygon points="80,12 60,8 60,16" fill="#c0c0c0" />
            {/* Body */}
            <rect x="10" y="8" width="50" height="8" rx="2" fill="url(#dartGrad)" />
            {/* Feathers */}
            <polygon points="10,12 0,4 5,12 0,20" fill="#22c55e" />
            <polygon points="15,12 5,6 10,12 5,18" fill="#16a34a" />
          </svg>
          {/* Trail */}
          <motion.div
            className="absolute right-full top-1/2 -translate-y-1/2 w-20 h-1 bg-gradient-to-l from-red-400 to-transparent"
            animate={{ opacity: [0.8, 0] }}
            transition={{ duration: 0.3 }}
          />
        </motion.div>
      );
    }

    // Net Launcher
    if (weaponType.includes('net')) {
      return (
        <motion.div
          className="absolute inset-0"
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1.5, opacity: 1 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
        >
          <svg viewBox="0 0 200 200" className="w-full h-full">
            <defs>
              <pattern id="netCapture" x="0" y="0" width="25" height="25" patternUnits="userSpaceOnUse">
                <path d="M 0 12.5 L 12.5 0 L 25 12.5 L 12.5 25 Z" fill="none" stroke="#fbbf24" strokeWidth="3" />
              </pattern>
            </defs>
            <motion.circle
              cx="100" cy="100" r="90"
              fill="url(#netCapture)"
              initial={{ r: 0 }}
              animate={{ r: 90 }}
              transition={{ duration: 0.5 }}
            />
            {/* Net ropes */}
            {[0, 45, 90, 135, 180, 225, 270, 315].map((angle, i) => (
              <motion.line
                key={i}
                x1="100" y1="100"
                x2={100 + 95 * Math.cos(angle * Math.PI / 180)}
                y2={100 + 95 * Math.sin(angle * Math.PI / 180)}
                stroke="#fbbf24"
                strokeWidth="3"
                initial={{ pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
              />
            ))}
          </svg>
        </motion.div>
      );
    }

    // Sonic Disruptor
    if (weaponType.includes('sonic')) {
      return (
        <motion.div className="absolute inset-0 flex items-center justify-center">
          {[...Array(5)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute rounded-full border-4 border-purple-400"
              initial={{ width: 50, height: 50, opacity: 0.8 }}
              animate={{
                width: [50, 300],
                height: [50, 300],
                opacity: [0.8, 0],
              }}
              transition={{
                duration: 1,
                delay: i * 0.2,
                repeat: 2,
              }}
            />
          ))}
          {/* Sound wave lines */}
          <motion.div
            className="absolute text-6xl"
            animate={{ scale: [1, 1.2, 1], opacity: [1, 0.5, 1] }}
            transition={{ duration: 0.3, repeat: 6 }}
          >
            🔊
          </motion.div>
        </motion.div>
      );
    }

    // Electromagnetic Pulse
    if (weaponType.includes('electromagnetic') || weaponType.includes('emp')) {
      return (
        <motion.div className="absolute inset-0">
          {/* Electric bolts */}
          {[...Array(8)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute top-1/2 left-1/2"
              style={{
                transform: `rotate(${i * 45}deg)`,
                transformOrigin: 'center',
              }}
              initial={{ opacity: 0 }}
              animate={{ opacity: [0, 1, 0] }}
              transition={{ duration: 0.15, repeat: 10, delay: i * 0.05 }}
            >
              <svg width="150" height="40" viewBox="0 0 150 40">
                <path
                  d="M0,20 L30,10 L50,25 L70,5 L90,30 L110,15 L130,20 L150,20"
                  stroke="#60a5fa"
                  strokeWidth="4"
                  fill="none"
                  strokeLinecap="round"
                />
              </svg>
            </motion.div>
          ))}
          {/* Central pulse */}
          <motion.div
            className="absolute inset-0 flex items-center justify-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: [0, 1, 0] }}
            transition={{ duration: 0.2, repeat: 8 }}
          >
            <div className="w-40 h-40 rounded-full bg-blue-400/50 blur-xl" />
          </motion.div>
          <motion.div
            className="absolute inset-0 flex items-center justify-center text-6xl"
            animate={{ scale: [1, 1.5, 1] }}
            transition={{ duration: 0.2, repeat: 5 }}
          >
            ⚡
          </motion.div>
        </motion.div>
      );
    }

    // Cryo Freezer
    if (weaponType.includes('cryo') || weaponType.includes('freeze')) {
      return (
        <motion.div className="absolute inset-0">
          {/* Ice crystals */}
          {[...Array(12)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute text-4xl"
              style={{
                left: `${20 + Math.random() * 60}%`,
                top: `${20 + Math.random() * 60}%`,
              }}
              initial={{ opacity: 0, scale: 0, rotate: 0 }}
              animate={{
                opacity: [0, 1, 1],
                scale: [0, 1.5, 1],
                rotate: [0, 180],
              }}
              transition={{ duration: 0.8, delay: i * 0.1 }}
            >
              ❄️
            </motion.div>
          ))}
          {/* Frost overlay */}
          <motion.div
            className="absolute inset-0 bg-gradient-radial from-cyan-300/60 via-blue-400/40 to-transparent rounded-full"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
          />
          {/* Ice mist */}
          <motion.div
            className="absolute inset-0 bg-white/30 backdrop-blur-sm rounded-full"
            initial={{ opacity: 0 }}
            animate={{ opacity: [0, 0.6, 0.4] }}
            transition={{ duration: 1 }}
          />
        </motion.div>
      );
    }

    // Taser Grid
    if (weaponType.includes('taser') || weaponType.includes('grid')) {
      return (
        <motion.div className="absolute inset-0">
          {/* Electric grid */}
          <svg className="absolute inset-0 w-full h-full" viewBox="0 0 200 200">
            {/* Horizontal lines */}
            {[40, 80, 120, 160].map((y, i) => (
              <motion.line
                key={`h${i}`}
                x1="20" y1={y} x2="180" y2={y}
                stroke="#fbbf24"
                strokeWidth="3"
                initial={{ pathLength: 0, opacity: 0 }}
                animate={{ pathLength: 1, opacity: [0, 1, 0.5] }}
                transition={{ duration: 0.3, delay: i * 0.1 }}
              />
            ))}
            {/* Vertical lines */}
            {[40, 80, 120, 160].map((x, i) => (
              <motion.line
                key={`v${i}`}
                x1={x} y1="20" x2={x} y2="180"
                stroke="#fbbf24"
                strokeWidth="3"
                initial={{ pathLength: 0, opacity: 0 }}
                animate={{ pathLength: 1, opacity: [0, 1, 0.5] }}
                transition={{ duration: 0.3, delay: 0.4 + i * 0.1 }}
              />
            ))}
          </svg>
          {/* Sparks at intersections */}
          {[...Array(6)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-4 h-4 bg-yellow-300 rounded-full blur-sm"
              style={{
                left: `${25 + (i % 3) * 25}%`,
                top: `${30 + Math.floor(i / 3) * 30}%`,
              }}
              animate={{ opacity: [0, 1, 0], scale: [0.5, 1.5, 0.5] }}
              transition={{ duration: 0.2, repeat: 10, delay: i * 0.1 }}
            />
          ))}
        </motion.div>
      );
    }

    // Sky Net Drone
    if (weaponType.includes('drone') || weaponType.includes('sky')) {
      return (
        <motion.div className="absolute inset-0">
          {/* Drone */}
          <motion.div
            className="absolute"
            initial={{ x: -100, y: -100, opacity: 0 }}
            animate={{ x: 80, y: 20, opacity: 1 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            <svg width="80" height="60" viewBox="0 0 80 60">
              {/* Drone body */}
              <ellipse cx="40" cy="30" rx="20" ry="10" fill="#374151" />
              {/* Propellers */}
              <motion.g
                animate={{ rotate: 360 }}
                transition={{ duration: 0.1, repeat: Infinity, ease: "linear" }}
                style={{ transformOrigin: "15px 20px" }}
              >
                <ellipse cx="15" cy="20" rx="12" ry="3" fill="#6b7280" />
              </motion.g>
              <motion.g
                animate={{ rotate: 360 }}
                transition={{ duration: 0.1, repeat: Infinity, ease: "linear" }}
                style={{ transformOrigin: "65px 20px" }}
              >
                <ellipse cx="65" cy="20" rx="12" ry="3" fill="#6b7280" />
              </motion.g>
              {/* Camera */}
              <circle cx="40" cy="35" r="5" fill="#ef4444" />
              <circle cx="40" cy="35" r="2" fill="#000" />
            </svg>
          </motion.div>
          {/* Dropping net */}
          <motion.div
            className="absolute left-1/2 -translate-x-1/2"
            initial={{ top: 60, opacity: 0 }}
            animate={{ top: 100, opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.5 }}
          >
            <svg width="200" height="150" viewBox="0 0 200 150">
              <defs>
                <pattern id="droneNet" x="0" y="0" width="20" height="20" patternUnits="userSpaceOnUse">
                  <path d="M 0 10 L 10 0 L 20 10 L 10 20 Z" fill="none" stroke="#9ca3af" strokeWidth="2" />
                </pattern>
              </defs>
              <motion.path
                d="M 20 0 Q 100 50 180 0 L 180 100 Q 100 150 20 100 Z"
                fill="url(#droneNet)"
                initial={{ scaleY: 0 }}
                animate={{ scaleY: 1 }}
                transition={{ duration: 0.4, delay: 0.6 }}
              />
            </svg>
          </motion.div>
        </motion.div>
      );
    }

    // Default - generic capture beam
    return (
      <motion.div
        className="absolute inset-0 flex items-center justify-center"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
      >
        <motion.div
          className="w-32 h-32 rounded-full bg-gradient-to-r from-green-400 to-cyan-400"
          animate={{
            scale: [1, 2, 1],
            opacity: [0.8, 0.4, 0.8],
          }}
          transition={{ duration: 0.5, repeat: 3 }}
        />
      </motion.div>
    );
  };

  // Dinosaur hit reaction
  const DinoHitEffect = () => {
    const weaponType = weapon?.toLowerCase() || '';

    // Frozen effect for cryo
    if (weaponType.includes('cryo')) {
      return (
        <motion.div
          className="absolute inset-0 rounded-2xl overflow-hidden"
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.7 }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-cyan-200/80 to-blue-300/80" />
          <div className="absolute inset-0 backdrop-blur-sm" />
        </motion.div>
      );
    }

    // Electric shock effect
    if (weaponType.includes('taser') || weaponType.includes('emp') || weaponType.includes('electromagnetic')) {
      return (
        <motion.div
          className="absolute inset-0"
          animate={{ opacity: [0, 1, 0] }}
          transition={{ duration: 0.1, repeat: 10 }}
        >
          <div className="absolute inset-0 bg-yellow-300/50 rounded-2xl" />
        </motion.div>
      );
    }

    return null;
  };

  // Cage SVG
  const CageIcon = ({ className }) => (
    <svg viewBox="0 0 100 100" className={className} fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="10" y="20" width="80" height="70" rx="5" strokeWidth="3" />
      <line x1="25" y1="20" x2="25" y2="90" />
      <line x1="40" y1="20" x2="40" y2="90" />
      <line x1="55" y1="20" x2="55" y2="90" />
      <line x1="70" y1="20" x2="70" y2="90" />
      <line x1="85" y1="20" x2="85" y2="90" />
      <path d="M 10 20 Q 50 0 90 20" strokeWidth="3" />
      <circle cx="50" cy="90" r="5" fill="currentColor" />
    </svg>
  );

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.5 }}
          className="fixed inset-0 z-50 flex items-center justify-center overflow-hidden"
          style={{
            background: isSuccess
              ? 'radial-gradient(circle at center, rgba(15, 23, 42, 0.98) 0%, rgba(2, 6, 23, 0.99) 100%)'
              : 'radial-gradient(circle at center, rgba(50, 20, 20, 0.98) 0%, rgba(20, 5, 5, 0.99) 100%)',
          }}
        >
          {/* Ambient particles */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            {[...Array(20)].map((_, i) => (
              <motion.div
                key={i}
                className={`absolute w-1 h-1 rounded-full ${isSuccess ? 'bg-green-400' : 'bg-red-400'}`}
                animate={{
                  y: [0, -500],
                  opacity: [0, 1, 0],
                }}
                transition={{
                  duration: 3 + Math.random() * 2,
                  delay: Math.random() * 3,
                  repeat: Infinity,
                }}
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `${70 + Math.random() * 30}%`,
                }}
              />
            ))}
          </div>

          <div className="relative flex flex-col items-center">
            {/* Weapon name indicator */}
            {phase < 4 && weapon && (
              <motion.div
                className="absolute -top-16 left-1/2 -translate-x-1/2 px-6 py-2 bg-yellow-500/20 border border-yellow-500/50 rounded-full"
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <span className="text-yellow-400 font-bold text-lg">🎯 {weapon}</span>
              </motion.div>
            )}

            {/* Main Animation Area */}
            <div className="relative w-80 h-80 mb-6">
              {isSuccess ? (
                // SUCCESS ANIMATION SEQUENCE
                <>
                  {/* Crosshair/Aiming */}
                  {phase === 0 && (
                    <motion.div
                      className="absolute inset-0 flex items-center justify-center"
                      initial={{ opacity: 0, scale: 1.5 }}
                      animate={{ opacity: 1, scale: 1 }}
                    >
                      <svg viewBox="0 0 100 100" className="w-64 h-64 text-red-500">
                        <motion.circle
                          cx="50" cy="50" r="40"
                          fill="none" stroke="currentColor" strokeWidth="2"
                          strokeDasharray="10 5"
                          animate={{ rotate: 360 }}
                          transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                        />
                        <line x1="50" y1="5" x2="50" y2="25" stroke="currentColor" strokeWidth="2" />
                        <line x1="50" y1="75" x2="50" y2="95" stroke="currentColor" strokeWidth="2" />
                        <line x1="5" y1="50" x2="25" y2="50" stroke="currentColor" strokeWidth="2" />
                        <line x1="75" y1="50" x2="95" y2="50" stroke="currentColor" strokeWidth="2" />
                        <circle cx="50" cy="50" r="5" fill="currentColor" />
                      </svg>
                    </motion.div>
                  )}

                  {/* Dinosaur target */}
                  {dinosaurImage && phase < 3 && (
                    <motion.div
                      className="absolute inset-0 flex items-center justify-center"
                      animate={phase === 2 ? {
                        x: [0, -10, 10, -5, 5, 0],
                        rotate: [0, -5, 5, -3, 3, 0],
                      } : {}}
                      transition={{ duration: 0.5 }}
                    >
                      <motion.img
                        src={dinosaurImage}
                        alt={dinosaurName}
                        className="w-52 h-52 object-cover rounded-2xl shadow-2xl border-4 border-white/20"
                        initial={{ scale: phase === 0 ? 0.8 : 1 }}
                        animate={{ scale: 1 }}
                      />
                      {/* Hit effect overlay */}
                      {phase === 2 && <DinoHitEffect />}
                    </motion.div>
                  )}

                  {/* Weapon firing effect */}
                  {phase === 1 && <WeaponEffect />}

                  {/* Hit impact */}
                  {phase === 2 && (
                    <motion.div
                      className="absolute inset-0"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                    >
                      <WeaponEffect />
                    </motion.div>
                  )}

                  {/* Capture - dinosaur shrinks into cage */}
                  {phase >= 3 && (
                    <>
                      {/* Dinosaur shrinking */}
                      {dinosaurImage && (
                        <motion.img
                          src={dinosaurImage}
                          alt={dinosaurName}
                          className="absolute left-1/2 top-1/2 w-52 h-52 object-cover rounded-2xl"
                          initial={{ x: '-50%', y: '-50%', scale: 1, opacity: 1 }}
                          animate={{ x: '-50%', y: '-30%', scale: 0.25, opacity: 0.8 }}
                          transition={{ duration: 0.6 }}
                        />
                      )}

                      {/* Cage appearing */}
                      <motion.div
                        className="absolute inset-0 flex items-center justify-center"
                        initial={{ scale: 0, rotate: -180, opacity: 0 }}
                        animate={{ scale: 1, rotate: 0, opacity: 1 }}
                        transition={{ duration: 0.6, delay: 0.3, ease: "backOut" }}
                      >
                        <CageIcon className="w-48 h-48 text-yellow-400 drop-shadow-lg" />
                        {dinosaurImage && (
                          <motion.img
                            src={dinosaurImage}
                            alt={dinosaurName}
                            className="absolute w-20 h-20 object-cover rounded-lg"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 0.9 }}
                            transition={{ delay: 0.7 }}
                          />
                        )}
                      </motion.div>

                      {/* Success burst */}
                      <motion.div
                        className="absolute inset-0 flex items-center justify-center pointer-events-none"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                      >
                        {[...Array(8)].map((_, i) => (
                          <motion.div
                            key={i}
                            className="absolute w-3 h-3 bg-green-400 rounded-full"
                            initial={{ x: 0, y: 0, opacity: 1 }}
                            animate={{
                              x: Math.cos(i * 45 * Math.PI / 180) * 150,
                              y: Math.sin(i * 45 * Math.PI / 180) * 150,
                              opacity: 0,
                            }}
                            transition={{ duration: 0.6, delay: 0.5 }}
                          />
                        ))}
                      </motion.div>
                    </>
                  )}
                </>
              ) : (
                // FAILURE ANIMATION
                <>
                  {/* Failed shot indicator */}
                  {phase === 0 && (
                    <motion.div
                      className="absolute inset-0 flex items-center justify-center"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                    >
                      <svg viewBox="0 0 100 100" className="w-64 h-64 text-red-500">
                        <motion.circle
                          cx="50" cy="50" r="40"
                          fill="none" stroke="currentColor" strokeWidth="2"
                          strokeDasharray="10 5"
                          animate={{ rotate: -360 }}
                          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                        />
                        <motion.line
                          x1="20" y1="20" x2="80" y2="80"
                          stroke="currentColor" strokeWidth="4"
                          initial={{ pathLength: 0 }}
                          animate={{ pathLength: 1 }}
                          transition={{ duration: 0.5, delay: 0.5 }}
                        />
                        <motion.line
                          x1="80" y1="20" x2="20" y2="80"
                          stroke="currentColor" strokeWidth="4"
                          initial={{ pathLength: 0 }}
                          animate={{ pathLength: 1 }}
                          transition={{ duration: 0.5, delay: 0.7 }}
                        />
                      </svg>
                    </motion.div>
                  )}

                  {/* Dinosaur escaping */}
                  {dinosaurImage && (
                    <motion.div
                      className="absolute inset-0 flex items-center justify-center"
                      initial={{ x: 0, scale: 1 }}
                      animate={phase >= 1 ? {
                        x: [0, -30, 400],
                        scale: [1, 1.3, 0.5],
                        rotate: [0, -10, 15],
                        opacity: [1, 1, 0],
                      } : {}}
                      transition={{ duration: 1.5, ease: "easeIn" }}
                    >
                      <motion.img
                        src={dinosaurImage}
                        alt={dinosaurName}
                        className="w-52 h-52 object-cover rounded-2xl shadow-2xl border-4 border-red-500/50"
                        animate={phase === 0 ? { scale: [1, 1.05, 1] } : {}}
                        transition={{ duration: 0.3, repeat: Infinity }}
                      />
                    </motion.div>
                  )}

                  {/* Dust trail */}
                  {phase >= 1 && (
                    <motion.div className="absolute right-0 top-1/2 -translate-y-1/2">
                      {[...Array(8)].map((_, i) => (
                        <motion.div
                          key={i}
                          className="absolute w-10 h-10 bg-gray-500/30 rounded-full blur-sm"
                          initial={{ x: 0, opacity: 0, scale: 0 }}
                          animate={{ x: [0, -50 - i * 30], opacity: [0, 0.5, 0], scale: [0, 1.5, 2] }}
                          transition={{ duration: 1.2, delay: 0.3 + i * 0.1 }}
                          style={{ top: (i % 3) * 20 - 20 }}
                        />
                      ))}
                    </motion.div>
                  )}

                  {/* Escape indicator */}
                  {phase >= 2 && (
                    <motion.div
                      className="absolute inset-0 flex items-center justify-center"
                      initial={{ opacity: 0, scale: 0 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ type: "spring", bounce: 0.5 }}
                    >
                      <motion.div
                        className="text-8xl"
                        animate={{ rotate: [0, 10, -10, 0], scale: [1, 1.1, 1] }}
                        transition={{ duration: 0.5, repeat: Infinity }}
                      >
                        🦖💨
                      </motion.div>
                    </motion.div>
                  )}
                </>
              )}
            </div>

            {/* Result Text */}
            {phase >= 4 && (
              <motion.div
                className="text-center"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                <motion.div
                  className="mb-4"
                  animate={isSuccess ? { rotate: [0, 10, -10, 0] } : { scale: [1, 1.1, 1] }}
                  transition={{ duration: 0.5, repeat: Infinity, repeatDelay: 1 }}
                >
                  {isSuccess ? (
                    <CheckCircle className="w-16 h-16 mx-auto text-green-400" strokeWidth={1.5} />
                  ) : (
                    <XCircle className="w-16 h-16 mx-auto text-red-400" strokeWidth={1.5} />
                  )}
                </motion.div>

                <motion.h1
                  className="text-4xl font-bold text-white mb-2"
                  initial={{ scale: 0.5 }}
                  animate={{ scale: 1 }}
                  transition={{ type: "spring", bounce: 0.5 }}
                >
                  {isSuccess ? '🎯 Captured!' : '💨 Escaped!'}
                </motion.h1>

                <p className="text-xl text-white/80 mb-4">
                  {isSuccess
                    ? `${dinosaurName || 'The dinosaur'} is secured!`
                    : `${dinosaurName || 'The dinosaur'} got away!`}
                </p>

                {testResults && (
                  <motion.div
                    className="mb-4"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.3 }}
                  >
                    <div className={`inline-flex items-center gap-2 px-5 py-2 rounded-full ${
                      isSuccess ? 'bg-green-500/20' : 'bg-red-500/20'
                    }`}>
                      <CheckCircle className={`w-5 h-5 ${isSuccess ? 'text-green-400' : 'text-red-400'}`} />
                      <span className="text-lg font-bold text-white">
                        {testResults.passedTests}/{testResults.totalTests} Tests Passed
                      </span>
                    </div>
                  </motion.div>
                )}

                <p className="text-white/60">Returning to map...</p>

                <div className="mt-4 w-56 mx-auto">
                  <div className="h-1 bg-white/20 rounded-full overflow-hidden">
                    <motion.div
                      className={`h-full rounded-full ${isSuccess ? 'bg-green-400' : 'bg-red-400'}`}
                      initial={{ width: '100%' }}
                      animate={{ width: '0%' }}
                      transition={{ duration: (autoNavigateDelay - 4000) / 1000, ease: 'linear' }}
                    />
                  </div>
                </div>
              </motion.div>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ResultOverlay;
